import { useEffect, useState, type ReactNode } from "react";
import type {
  Artifact,
  RepositoryContextDefinition,
  SkillDefinition,
} from "@lumen/shared";
import { api } from "../../api";
import type { ComposerSkill } from "../composer/Composer";
import { useConversationArtifacts } from "../artifacts/useConversationArtifacts";
import { ChatMentionsContext, type Mention } from "./ChatMentionsContext";

export function ChatMentionsProvider({
  conversationId,
  revision,
  onOpen,
  children,
}: {
  conversationId?: string;
  revision: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  const [skills, setSkills] = useState<ComposerSkill[]>([]);
  const [vaultArtifacts, setArtifacts] = useState<Artifact[]>([]);
  const { artifacts: conversationArtifacts } = useConversationArtifacts(
    conversationId,
    revision,
  );
  const artifacts = [
    ...new Map(
      [...vaultArtifacts, ...(conversationArtifacts ?? [])].map((artifact) => [
        artifact.id,
        artifact,
      ]),
    ).values(),
  ];
  const [selected, setSelected] = useState<Mention>();
  useEffect(() => {
    let active = true;
    void Promise.all([
      api<SkillDefinition[]>("/skills"),
      conversationId
        ? api<RepositoryContextDefinition>(
            `/conversations/${conversationId}/repository-context`,
          ).catch(() => null)
        : null,
    ])
      .then(([rows, repository]) => {
        if (active)
          setSkills([
            ...(Array.isArray(rows) ? rows : []),
            ...(repository?.skills ?? []),
          ]);
      })
      .catch(() => undefined);
    void api<Artifact[]>("/artifacts")
      .then((rows) => {
        if (active) setArtifacts(Array.isArray(rows) ? rows : []);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [conversationId, revision]);
  return (
    <ChatMentionsContext.Provider
      value={{
        skills,
        artifacts,
        selected,
        open: (mention) => {
          setSelected(mention);
          onOpen();
        },
      }}
    >
      {children}
    </ChatMentionsContext.Provider>
  );
}
